import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Globe, MapPin } from 'lucide-react';
import { 
  detectCountryFromIP, 
  getLanguageFromCountry, 
  hasLanguagePreference,
  markGeoLanguagePrompted,
  getLanguageName,
  getLanguageFlag
} from '@/lib/geoLanguage';

export default function GeoLanguagePrompt() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [detectedLanguage, setDetectedLanguage] = useState(null);
  const [detectedCountry, setDetectedCountry] = useState(null);

  useEffect(() => {
    // Only run detection if user hasn't set a preference yet
    if (hasLanguagePreference()) {
      return;
    }

    const detectLanguage = async () => {
      try {
        const country = await detectCountryFromIP();
        if (country) {
          setDetectedCountry(country);
          const language = getLanguageFromCountry(country);
          
          // Only show prompt if detected language is different from current
          if (language && language !== i18n.language && language !== 'en') {
            setDetectedLanguage(language);
            setIsOpen(true);
          } else {
            // Mark as prompted even if we don't show dialog
            markGeoLanguagePrompted();
          }
        }
      } catch (error) {
        console.warn('Language detection error:', error);
        markGeoLanguagePrompted();
      }
    };

    // Delay detection slightly to not block initial render
    const timer = setTimeout(detectLanguage, 1500);
    return () => clearTimeout(timer);
  }, [i18n.language]);

  const handleAccept = () => {
    if (detectedLanguage) {
      i18n.changeLanguage(detectedLanguage);
    }
    markGeoLanguagePrompted();
    setIsOpen(false);
  };

  const handleDecline = () => {
    markGeoLanguagePrompted();
    setIsOpen(false);
  };

  if (!detectedLanguage) return null;

  const flag = getLanguageFlag(detectedLanguage);
  const langName = getLanguageName(detectedLanguage);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md" data-testid="geo-language-prompt">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-xl">
            <div className="p-2 bg-primary/10 rounded-full">
              <Globe className="w-5 h-5 text-primary" />
            </div>
            <span>Language Detected</span>
          </DialogTitle>
          <DialogDescription className="text-base mt-2 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-muted-foreground" />
            Based on your location
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-6">
          <div className="text-center p-6 bg-muted/50 rounded-xl border">
            <span className="text-5xl mb-4 block">{flag}</span>
            <p className="text-lg font-medium mb-1">
              Would you like to use <span className="text-primary">{langName}</span>?
            </p>
            <p className="text-sm text-muted-foreground">
              We detected you may prefer {langName} based on your location
            </p>
          </div>
        </div>

        <DialogFooter className="flex gap-3 sm:gap-3">
          <Button 
            variant="outline" 
            onClick={handleDecline}
            className="flex-1"
            data-testid="geo-language-decline"
          >
            Keep English
          </Button>
          <Button 
            onClick={handleAccept}
            className="flex-1"
            data-testid="geo-language-accept"
          >
            Use {langName}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
